import React, { Fragment, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Box, Link as MuiLink, Stack, Typography } from "@mui/material";

import { mfaValidate, validateUserRole } from "../../action/auth";
import Alert from "../layout/Alert";
import customConfig from "../../config";

import Dialog from "../Common/Dailog/Dailog";
import AppButton from "../Common/Button/Button";
import FormInput from "../Common/FormInputs/MuiTextField";

const formMaxWidth = 360;

const MfaValidate = ({ mfaValidate, tenantData, mfaToken, validateUserRole }) => {
    const [mfaCode, setMfaCode] = useState("");
    const [error, setError] = useState("");
    const [showInstructions, setShowInstructions] = useState(false); 

    const handleInstructionsClick = (e) => {
        e?.preventDefault?.();
        setShowInstructions(true);
    };

    const closeInstructions = () => {
        setShowInstructions(false);
    };

    const handleChange = (e) => {
      const value = e.target.value;
      if (/^\d{0,6}$/.test(value)) {
        setMfaCode(value);
        if (error) setError("");
      }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!mfaCode) {
            setError("MFA Code is required");
            return;
        }
        mfaValidate(mfaCode, mfaToken)
          .then(async (res) => {
            const token =
              res?.data?.accessToken ||
              res?.data?.accesstoken ||
              res?.data?.AccessToken;
            if (token) {
              await validateUserRole(res);
              return;
            }
            setError(customConfig.authMsg || "Login failed. Please try again.");
          })
          .catch((err) => {
            console.error("Login Error: ", err);
            setError(
              err?.response?.data?.message ||
                err?.response?.data?.error ||
                err?.message ||
                customConfig.mfaInvalid
            );
          });
    };

    return (
        <Fragment>
            <div className="login-wrapper">
                <div className="login-box">
                    <div className="form-box">
                        <div className="row mb-0">
                            <Alert />
                            <div className="col s12 center-align">
                                {tenantData?.tenantLogoUrl && (
                                    <img
                                        src={tenantData.tenantLogoUrl}
                                        alt="Logo"
                                        className="logo-outer"
                                        onError={(e) => {
                                            e.currentTarget.style.display = "none";
                                        }}
                                    />
                                )}
                            </div>
                        </div>

                        <Typography
                            variant="h6"
                            sx={{
                                textAlign: "center",
                                maxWidth: { xs: "100%", sm: formMaxWidth },
                                mx: "auto",
                                mb: 2,
                                fontSize: "clamp(0.72rem, 2.9vw, 0.95rem)",
                                lineHeight: 1.2,
                                whiteSpace: "nowrap",
                            }}
                        >
                            Enter 2-Step Authentication code
                        </Typography>

                        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: "100%" }}>
                            <Stack
                                spacing={2}
                                sx={{
                                    width: "100%",
                                    maxWidth: { xs: "100%", sm: formMaxWidth },
                                    mx: "auto",
                                    px: { xs: 0, sm: 0 },
                                }}
                            >
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Code *
                                </Typography>

                                <FormInput
                                    id="mfaCode"
                                    name="mfaCode"
                                    type="text"
                                    value={mfaCode}
                                    onChange={handleChange}
                                    placeholder="Code"
                                    inputProps={{ maxLength: 6, inputMode: "numeric", pattern: "[0-9]*" }}
                                    required
                                    error={Boolean(error)}
                                    helperText={error || ""}
                                    touched="true"
                                    fullWidth
                                    sx={{
                                        "& .MuiOutlinedInput-root": { overflow: "visible" },
                                    }}
                                />

                                <MuiLink
                                    component="button"
                                    type="button"
                                    onClick={handleInstructionsClick}
                                    underline="none"
                                    sx={{
                                        color: "primary.main",
                                        fontWeight: 500,
                                        textAlign: "center",
                                        backgroundColor: "transparent",
                                        border: 0,
                                        padding: 0,
                                        margin: 0,
                                        lineHeight: 1.2,
                                        cursor: "pointer",
                                        "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
                                        "&:focus": { backgroundColor: "transparent" },
                                    }}
                                >
                                    Need help entering codes?
                                </MuiLink>

                                <AppButton
                                    type="submit"
                                    variant="primary"
                                    size="large"
                                    fullWidth
                                    disabled={!mfaCode}
                                    sx={{
                                        height: 50,
                                        fontSize: 17,
                                        fontWeight: 600,
                                        width: "100%",
                                    }}
                                >
                                    Validate
                                </AppButton>
                            </Stack>
                        </Box>

                        <Dialog
                            open={showInstructions}
                            onClose={closeInstructions}
                            maxWidth="sm"
                            fullWidth
                            showCloseButton
                            closeOnBackdropClick
                            closeOnEscapeKeyDown
                            title={undefined}
                            contentSx={{
                                pt: 0,
                                pb: 2,
                                overflow: "visible",
                            }}
                            PaperProps={{
                                sx: { borderRadius: 2, width: 680, maxWidth: "calc(100vw - 24px)" },
                            }}
                        >
                            <div className="qr-popup-container" style={{ maxWidth: "100%" }}>
                                <h2
                                    style={{
                                        overflow: "visible",
                                        maxWidth: "100%",
                                        fontSize: "clamp(20px, 5vw, 24px)",
                                        lineHeight: 1.2,
                                        display: "-webkit-box",
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: "vertical",
                                    }}
                                >
                                    How to Enter 2-Step Authentication Codes
                                </h2>
                                <ol>
                                    {(customConfig.mfaValidationInstructions || []).map((instruction, index) => (
                                        <li key={index}>{instruction}</li>
                                    ))}
                                </ol>
                            </div>
                        </Dialog>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

MfaValidate.propTypes = {
    mfaToken: PropTypes.string.isRequired,
    mfaValidate: PropTypes.func.isRequired,
    tenantData: PropTypes.object.isRequired,
    validateUserRole: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
    mfaToken: state.auth.mfaToken,
  });

const mapDispatchToProps = (dispatch) => ({
    mfaValidate: (otp, mfaToken) => dispatch(mfaValidate(otp, mfaToken)),
    validateUserRole: (res) => dispatch(validateUserRole(res)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MfaValidate);
